import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Upload, X } from "lucide-react";
import { HiChevronRight } from "react-icons/hi";
import MessagePopup from "./MessagePopup";
import apiEndpoints from "../../apiconfig";
import { useSnackbar } from "../../DynamicComponents/SnackbarContext";

// CSV.js (updated)
// - Left column: upload CSV and validation + rules (NO preview — only errors shown)
// - Right column: same Message Composer UI/behaviour as SingleMsg.js
// - Mapping rules: 1st header = phone, remaining headers map to template variables in order
// - When a template is selected we validate header count matches template variable count

export default function CSVMsg() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState([]);

  // CSV states
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState([]); // array of rows (each row is array of fields)
  const [csvErrors, setCsvErrors] = useState([]);

  // other UI states (like SingleMsg)
  const [mobileNumber, setMobileNumber] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [useDynamicHeader, setUseDynamicHeader] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [showProgress, setShowProgress] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [sentCount, setSentCount] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failCount, setFailCount] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Helper: CSV row splitter that handles quoted fields
  const splitCSVLine = (line) => {
    const result = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          // escaped quote
          cur += '"';
          i++; // skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === "," && !inQuotes) {
        result.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    result.push(cur);
    return result.map((f) => f.trim());
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
    if (lines.length === 0) return { headers: [], rows: [] };
    const headers = splitCSVLine(lines[0]).map((h) => h.trim());
    const rows = lines.slice(1).map((line) => splitCSVLine(line));
    return { headers, rows };
  };

  const validateCsvHeaders = (headers) => {
    const errs = [];
    if (!headers || headers.length === 0) {
      errs.push("CSV does not contain headers.");
      return errs;
    }
    const first = headers[0].toLowerCase().replace(/\s+/g, "");
    const allowedFirst = [
      "phone",
      "mobile",
      "phonenumber",
      "mobile_number",
      "phone_number",
      "number",
    ];
    if (!allowedFirst.includes(first)) {
      errs.push(
        "First header must be the phone number (e.g. 'phone' or 'mobile')."
      );
    }
    return errs;
  };

  const validateCsvRows = (headers, rows) => {
    const errs = [];
    const phoneIdx = 0;
    rows.forEach((row, rindex) => {
      // if row shorter than headers, pad
      const padded = [...row];
      while (padded.length < headers.length) padded.push("");
      // validate phone
      const rawPhone = (padded[phoneIdx] || "").toString();
      const digits = rawPhone.replace(/[^0-9]/g, "");
      if (digits.length !== 10) {
        errs.push(
          `Row ${rindex + 2}: Phone must be 10 digits (found '${rawPhone}').`
        );
      }
    });
    return errs;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      showSnackbar("Please upload a valid CSV file.");
      return;
    }
    if (file.size > 32 * 1024 * 1024) {
      showSnackbar("File size exceeds 32MB limit.");
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const { headers, rows } = parseCSV(text);
        const headerErrs = validateCsvHeaders(headers);
        const rowErrs = validateCsvRows(headers, rows);
        const allErrs = [...headerErrs, ...rowErrs];

        setCsvHeaders(headers);
        setCsvData(
          rows.map((r) => {
            // pad rows to headers length to avoid undefined
            const padded = [...r];
            while (padded.length < headers.length) padded.push("");
            return padded;
          })
        );
        setCsvErrors(allErrs);

        // if template is already selected, validate variable count
        if (selectedTemplate) {
          const expectedVars = templateVariables.length;
          const availableVars = Math.max(0, headers.length - 1);
          if (expectedVars !== availableVars) {
            setCsvErrors((prev) => [
              ...prev,
              `Template expects ${expectedVars} variables but CSV provides ${availableVars} columns (excluding phone).`,
            ]);
          }
        }

        if (allErrs.length === 0) {
          showSnackbar("CSV uploaded and parsed successfully.", "success");
        }
      } catch (err) {
        showSnackbar("Failed to parse CSV file.", "error");
        setCsvErrors(["Failed to parse CSV file. Ensure it is valid CSV."]);
      }
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileChange({ target: { files: [file] } });
  };

  const handleClear = () => {
    setMessageContent("");
    setMobileNumber("");
    setCountryCode("");
    setSelectedTemplate(null);
    setTemplateVariables([]);
    setSelectedFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setCsvErrors([]);
    setUseDynamicHeader(false);
    setErrors({});
  };

  const handleSelectTemplate = (template) => {
    let headerObj = null;
    let buttonsData = [];
 
    try {
      const templateJson = JSON.parse(template.template_json);
      const headerComp = templateJson.components?.find(c => c.type === "HEADER");
      if (headerComp && ["IMAGE", "VIDEO", "DOCUMENT", "AUDIO"].includes(headerComp.format)) {
        const mediaUrl = headerComp.example?.header_handle?.[0] || "";
        headerObj = {
          type: headerComp.format.toLowerCase(),
          link: mediaUrl,
          media_id: template.local_media?.media_id || null
        };
      }
      const buttonsComp = templateJson.components?.find(c => c.type === "BUTTONS");
      if (buttonsComp && Array.isArray(buttonsComp.buttons)) {
        buttonsData = buttonsComp.buttons;
      }
    } catch (err) {
      console.warn("Failed to parse template_json for header media", err);
    }
 
    setSelectedTemplate({
      ...template,
      header: headerObj,
      buttons: buttonsData,
    });

    setMessageContent(template.templateBody || template.content || "");
    setIsPopupOpen(false);

    const matches = (template.templateBody || "").match(/{{\d+}}/g) || [];
    const variableFields = matches.map((match) => ({
      placeholder: match,
      value: "",
      type: "text",
    }));
    setTemplateVariables(variableFields);

    if (csvHeaders.length > 0) {
      const expectedVars = variableFields.length;
      const availableVars = Math.max(0, csvHeaders.length - 1);
      if (expectedVars !== availableVars) {
        setCsvErrors((prev) => [
          ...prev.filter((p) => !p.startsWith("Template expects")),
          `Template expects ${expectedVars} variables but CSV provides ${availableVars} columns (excluding phone).`,
        ]);
      } else {
        setCsvErrors((prev) =>
          prev.filter((p) => !p.startsWith("Template expects"))
        );
      }
    }

    if (errors.messageContent) {
      setErrors((prev) => ({ ...prev, messageContent: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedTemplate) newErrors.template = "Please select a template";
    if (!selectedFile) newErrors.csv = "Please upload a CSV file";
    if (csvErrors.length > 0) newErrors.csv = "CSV has validation errors";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Send messages for all CSV rows
  const sendCSVMessages = async () => {
    if (!validateForm()) {
      showSnackbar("Please fix validation errors before sending.", "warning");
      return;
    }

    setLoading(true); // show loader ONLY once
    setShowProgress(true);

    setTotalCount(csvData.length);
    setSentCount(0);
    setSuccessCount(0);
    setFailCount(0);

    try {
      const headers = csvHeaders;
      const varHeaders = headers.slice(1);

      // Loop without blocking UI
      for (let i = 0; i < csvData.length; i++) {
        const row = csvData[i];

        const phoneRaw = (row[0] || "").toString();
        const phoneDigits = phoneRaw.replace(/[^0-9]/g, "");
        const fullPhone = (countryCode || "") + phoneDigits;

        const variables = varHeaders.map((_, idx) =>
          row[idx + 1] !== undefined ? row[idx + 1].toString() : "",
        );

        try {
          const response = await fetch(apiEndpoints.sendmessage, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({
              to: fullPhone,
              template_name: selectedTemplate.name,
              template_id: selectedTemplate.id,
              language: selectedTemplate.languageName || "en",
              variables,
              header: selectedTemplate.header
                ? {
                  type: selectedTemplate.header.type,
                  link: (useDynamicHeader || !selectedTemplate.header.media_id) ? selectedTemplate.header.link : null,
                  media_id: !useDynamicHeader ? selectedTemplate.header.media_id : null,
                  filename: selectedTemplate.header.filename || "Document.pdf"
                }
                : null,
              buttons: selectedTemplate.buttons || [],
              message_content: messageContent,
            }),
          });

          const result = await response.json();
          if (result.status === "success") {
            setSuccessCount((c) => c + 1);
          } else {
            setFailCount((c) => c + 1);
          }
        } catch {
          setFailCount((c) => c + 1);
        }

        // 🔥 THIS updates the UI progress
        setSentCount((c) => c + 1);
      }

      showSnackbar(
        `Done. Success: ${successCount}, Failed: ${failCount}`,
        "success",
      );
    } catch {
      showSnackbar("Unexpected error while sending messages.", "error");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div
      className="w-full bg-[#F4F5F7] p-4 sm:p-6"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: CSV Upload */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-md border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Upload CSV File
          </h3>

          <div className="bg-green-50 border border-green-100 rounded-lg p-3 mb-4 text-center">
            <p className="text-sm text-gray-700">
              CSV Rules:{" "}
              <span className="font-semibold text-[#25D366]">
                First column must be Phone Number
              </span>
              . Remaining columns will map to template variables in order.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Only .csv files allowed, max size 32MB.
            </p>
          </div>
          <div className="flex justify-center mb-4">
            <a
              href="/template_message.csv"
              download
              className="text-sm font-medium text-[#25D366] hover:underline"
            >
              Download Sample CSV
            </a>
          </div>

          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-[#25D366] transition-all ${csvErrors.length > 0
                ? "border-red-400 bg-red-50"
                : "border-gray-300"
              }`}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <label className="cursor-pointer block">
              <div className="flex flex-col items-center justify-center space-y-3">
                <Upload className="w-8 h-8 text-gray-400" />
                <p className="text-sm text-gray-600">
                  {selectedFile ? (
                    <span className="font-medium text-[#25D366]">
                      {selectedFile.name}
                    </span>
                  ) : (
                    "Drag & drop your CSV file or click to browse"
                  )}
                </p>
                <span className="text-xs text-gray-400">
                  Supports: .csv only
                </span>
                {selectedFile && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setCsvHeaders([]);
                      setCsvData([]);
                      setCsvErrors([]);
                    }}
                    className="mt-2 text-xs text-red-500 hover:text-red-700 flex items-center"
                  >
                    <X size={14} className="mr-1" /> Remove file
                  </button>
                )}
              </div>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>

            {/* Errors list (no preview requested) */}
            {csvErrors.length > 0 && (
              <div className="text-left mt-4 bg-white border border-red-100 rounded p-3">
                <h4 className="text-sm font-semibold text-red-600 mb-2">
                  CSV Errors
                </h4>
                <ul className="text-xs text-red-600 list-disc list-inside space-y-1">
                  {csvErrors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Header info (simple) */}
            {csvHeaders.length > 0 && (
              <div className="text-left mt-4 text-xs text-gray-600">
                <p className="font-semibold">Detected headers:</p>
                <ol className="list-decimal list-inside ml-4 mt-1">
                  {csvHeaders.map((h, i) => (
                    <li key={i}>{h || "(empty header)"}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* Country Code selector (used for sending) */}
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country Code
              </label>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#25D366] border-gray-300"
              >
                <option value="">Select</option>
                <option value="+91">India (+91)</option>
                <option value="+1">USA (+1)</option>
                <option value="+44">UK (+44)</option>
                <option value="+971">UAE (+971)</option>
              </select>
            </div>
          </div>
        </div>

        {/* RIGHT: Message Composer (keeps SingleMsg behavior) */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-md border border-gray-100 flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Message Composer
            </h3>

            {/* Message Content (readonly - uses popup to select template) */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Message Content
                </label>
                {messageContent && (
                  <button
                    onClick={() => setMessageContent("")}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <textarea
                readOnly
                onClick={() => setIsPopupOpen(true)}
                value={messageContent}
                placeholder="Click here to select a WhatsApp template"
                className={`w-full p-3 border rounded-lg h-40 bg-gray-50 cursor-pointer focus:ring-2 focus:ring-[#25D366] ${errors.messageContent ? "border-red-500" : "border-gray-300"
                  }`}
              />
            </div>

            {/* Header Mode Toggle */}
            {selectedTemplate && selectedTemplate.header && (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700 capitalize">
                    {selectedTemplate.header.type} Header
                  </span>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`font-medium ${!useDynamicHeader ? "text-[#25D366]" : "text-gray-400"}`}>Static</span>
                    <button
                      type="button"
                      onClick={() => setUseDynamicHeader(prev => !prev)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${useDynamicHeader ? "bg-[#25D366]" : "bg-gray-300"
                        }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${useDynamicHeader ? "translate-x-6" : "translate-x-1"
                        }`} />
                    </button>
                    <span className={`font-medium ${useDynamicHeader ? "text-[#25D366]" : "text-gray-400"}`}>Dynamic</span>
                  </div>
                </div>
                {!useDynamicHeader ? (
                  <p className="text-xs text-gray-500">The approved template {selectedTemplate.header.type} will be sent to all recipients.</p>
                ) : (
                  <>
                    <p className="text-xs text-gray-500 mb-2">Enter a custom public URL to send a different {selectedTemplate.header.type} to all recipients.</p>
                    <input
                      type="text"
                      placeholder={`Enter public ${selectedTemplate.header.type} URL (must be accessible on the internet)`}
                      value={selectedTemplate.header.link || ""}
                      onChange={(e) => {
                        setSelectedTemplate(prev => ({
                          ...prev,
                          header: { ...prev.header, link: e.target.value }
                        }));
                      }}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#25D366] focus:outline-none text-sm"
                    />
                  </>
                )}
              </div>
            )}

            {/* Template Variables (if template selected) */}
            {selectedTemplate && templateVariables.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Template Variables (values are taken from CSV for sending)
                </h4>
                <p className="text-xs text-gray-500 mb-2">
                  Note: CSV columns (excluding first phone column) are mapped to
                  these variables in order. You don't need to fill these
                  manually when sending from CSV.
                </p>
                {/* {templateVariables.map((variable, index) => (
                  <div
                    key={index}
                    className="flex flex-wrap sm:flex-nowrap items-center gap-2 border border-gray-200 p-2 rounded-lg bg-gray-50 mb-2"
                  >
                    <span className="font-mono text-gray-700 text-sm sm:w-16 w-auto sm:text-left text-center">
                      {variable.placeholder}
                    </span>
                    <input
                      type="text"
                      placeholder="(value from CSV)"
                      value={variable.value}
                      onChange={(e) => {
                        const updated = [...templateVariables];
                        updated[index].value = e.target.value;
                        setTemplateVariables(updated);
                      }}
                      className="flex-1 w-full border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-[#25D366] focus:outline-none"
                    />
                  </div>
                ))} */}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4">
            <button
              onClick={handleClear}
              className="w-full sm:w-auto px-5 py-2 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-100 transition"
              disabled={loading}
            >
              Clear
            </button>
            <button
              onClick={sendCSVMessages}
              className="w-full sm:w-auto px-6 py-2 flex items-center justify-center bg-[#25D366] text-white rounded-lg hover:bg-[#1CB85C] transition disabled:bg-gray-400"
              disabled={
                loading ||
                csvErrors.length > 0 ||
                !selectedTemplate ||
                !csvData.length
              }
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>Send Messages</>
              )}
            </button>
          </div>
        </div>
      </div>

      {showProgress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm text-center">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Sending Messages
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              {sentCount} / {totalCount} sent
            </p>

            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div
                className="bg-[#25D366] h-3 rounded-full transition-all"
                style={{
                  width: `${(sentCount / totalCount) * 100 || 0}%`,
                }}
              />
            </div>

            <div className="text-xs text-gray-500 space-y-1">
              <p>✅ Success: {successCount}</p>
              <p>❌ Failed: {failCount}</p>
            </div>

            {sentCount === totalCount && (
              <button
                onClick={() => {
                  setShowProgress(false);
                  if (failCount === 0) handleClear();
                }}
                className="mt-4 px-4 py-2 bg-[#25D366] text-white rounded-lg"
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}

      {/* Template Popup */}
      {isPopupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-auto">
          <div className="w-full flex justify-center items-start mt-10 md:items-center">
            <MessagePopup
              onClose={() => setIsPopupOpen(false)}
              onSelectTemplate={handleSelectTemplate}
            />
          </div>
        </div>
      )}
    </div>
  );
}
